import { Skeleton } from "@/components/skeleton";
import { cn } from "@/lib/utils";
import { formatMonthDay } from "@/lib/i18n";
import type { TaskAccountabilitySummary } from "@/hooks/use-tasks";
import { getCrmTaskDefinition } from "@ease/shared/crm-task-registry";

interface CrmTaskAccountabilityPanelProps {
  summary: TaskAccountabilitySummary | null;
  isLoading: boolean;
  error: string | null;
  memberLabelById: Map<string, string>;
}

export function CrmTaskAccountabilityPanel({
  summary,
  isLoading,
  error,
  memberLabelById,
}: CrmTaskAccountabilityPanelProps) {
  if (isLoading && !summary) {
    return (
      <div className="grid gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-3 md:grid-cols-5">
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton key={index} className="h-16 rounded-md" />
        ))}
      </div>
    );
  }

  if (!summary || error || summary.meta.degraded) {
    return (
      <div className="rounded-lg border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 px-4 py-3 text-sm text-[var(--color-warning)]">
        Task quality signals unavailable. The queue still works; accountability analytics will retry on refresh.
      </div>
    );
  }

  const totals = summary.totals;
  const rates = summary.rates;
  const riskSignals = [
    totals.noAnswerWithoutNotes > 0 ? `${totals.noAnswerWithoutNotes} no-answer completion${totals.noAnswerWithoutNotes === 1 ? "" : "s"} without notes` : null,
    totals.veryFastCompletions > 0 ? `${totals.veryFastCompletions} very-fast completion${totals.veryFastCompletions === 1 ? "" : "s"} to review` : null,
    totals.followupsCreated > 0 ? `${totals.followupsCreated} follow-up task${totals.followupsCreated === 1 ? "" : "s"} created from outcomes` : null,
  ].filter((signal): signal is string => Boolean(signal));
  const staffNeedingAttention = summary.staff.filter((staff) => staff.attentionFlags.length > 0).slice(0, 3);
  const typeBreakdowns = (summary.breakdowns?.byType ?? []).slice(0, 4);
  const outcomeBreakdowns = (summary.breakdowns?.byOutcome ?? []).slice(0, 4);

  return (
    <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
            Task accountability
          </p>
          <h2 className="mt-1 text-base font-semibold text-[var(--color-foreground)]">
            Queue quality over the last {summary.windowDays} days
          </h2>
        </div>
        <p className="text-xs text-[var(--color-muted-foreground)]">
          Updated {formatMonthDay(summary.generatedAt)}
        </p>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-5">
        <CrmTaskAccountabilityMetric label="Open" value={String(totals.openTasks)} detail={`${totals.dueSoonTasks} due soon`} />
        <CrmTaskAccountabilityMetric label="Overdue" value={String(totals.overdueTasks)} detail={`${formatPercent(rates.overduePressurePct)} of open queue`} tone={totals.overdueTasks > 0 ? "risk" : "default"} />
        <CrmTaskAccountabilityMetric label="Completed" value={String(totals.completedTasks)} detail={`${totals.skippedTasks} skipped`} />
        <CrmTaskAccountabilityMetric label="Evidence" value={formatPercent(rates.completionEvidencePct)} detail={`${totals.completionsWithoutNotes} missing notes`} tone={rates.completionEvidencePct < 80 && totals.completedTasks > 0 ? "risk" : "default"} />
        <CrmTaskAccountabilityMetric label="Auto-resolved" value={formatPercent(rates.autoResolutionPct)} detail={`${totals.autoCancelledTasks} auto-cancelled`} />
      </div>
      {(riskSignals.length > 0 || staffNeedingAttention.length > 0) && (
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          {riskSignals.length > 0 && (
            <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
                Quality signals
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {riskSignals.map((signal) => (
                  <span key={signal} className="rounded-full border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 px-2 py-1 text-xs text-[var(--color-warning)]">
                    {signal}
                  </span>
                ))}
              </div>
            </div>
          )}
          {staffNeedingAttention.length > 0 && (
            <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
                Staff attention
              </p>
              <div className="mt-2 space-y-2">
                {staffNeedingAttention.map((staff) => (
                  <p key={staff.staffId} className="text-xs text-[var(--color-muted-foreground)]">
                    <span className="font-medium text-[var(--color-foreground)]">{formatMemberLabel(staff.staffId, memberLabelById) ?? staff.staffId}</span>: {staff.attentionFlags.join(", ")}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {(typeBreakdowns.length > 0 || outcomeBreakdowns.length > 0) && (
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          {typeBreakdowns.length > 0 && (
            <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
                Task family pressure
              </p>
              <div className="mt-2 space-y-2">
                {typeBreakdowns.map((item) => {
                  const label = getCrmTaskDefinition(item.taskType).label;
                  return (
                    <CrmTaskAccountabilityBreakdownRow
                      key={item.taskType}
                      label={label}
                      primary={`${item.openTasks} open`}
                      detail={`${item.overdueTasks} overdue · ${item.completedTasks} completed · ${item.completionsWithoutNotes} weak`}
                      riskPct={item.qualityIssuePct}
                    />
                  );
                })}
              </div>
            </div>
          )}
          {outcomeBreakdowns.length > 0 && (
            <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
                Completion outcome quality
              </p>
              <div className="mt-2 space-y-2">
                {outcomeBreakdowns.map((item) => (
                  <CrmTaskAccountabilityBreakdownRow
                    key={item.outcome}
                    label={formatTaskLabel(item.outcome) ?? item.outcome}
                    primary={`${item.completedTasks} completed`}
                    detail={`${item.completionsWithoutNotes} missing notes · ${item.veryFastCompletions} very fast · ${formatPercent(item.completionEvidencePct)} evidence`}
                    riskPct={item.qualityIssuePct}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function CrmTaskAccountabilityBreakdownRow({
  label,
  primary,
  detail,
  riskPct,
}: {
  label: string;
  primary: string;
  detail: string;
  riskPct: number;
}) {
  return (
    <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-[var(--color-foreground)]">{label}</p>
        <span className={cn(
          "rounded-full border px-2 py-0.5 text-[11px] font-medium",
          riskPct > 0
            ? "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]"
            : "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]",
        )}>
          {formatPercent(riskPct)} risk
        </span>
      </div>
      <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
        {primary} · {detail}
      </p>
    </div>
  );
}

function CrmTaskAccountabilityMetric({
  label,
  value,
  detail,
  tone = "default",
}: {
  label: string;
  value: string;
  detail: string;
  tone?: "default" | "risk";
}) {
  return (
    <div className={cn(
      "rounded-md border bg-[var(--color-surface)] px-3 py-2",
      tone === "risk" ? "border-[var(--color-warning)]/40" : "border-[var(--color-border)]",
    )}>
      <p className="text-xs font-medium text-[var(--color-muted-foreground)]">{label}</p>
      <p className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">{value}</p>
      <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{detail}</p>
    </div>
  );
}

function formatPercent(value: number): string {
  return `${Math.round(value)}%`;
}

function formatMemberLabel(memberId: string | null | undefined, memberLabelById: Map<string, string>): string | null {
  if (!memberId) return null;
  return memberLabelById.get(memberId) ?? null;
}

function formatTaskLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  return value
    .split(/[_-]/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
